/** Unwrap SDK / API envelopes so we always work with the v4 CAS shape. */
export function unwrapCasPayload(data) {
  if (!data || typeof data !== 'object') return {};
  if (data.mutual_funds || data.demat_accounts || data.investor || data.meta || data.nps || data.insurance) {
    return data;
  }
  if (data.raw_response) return unwrapCasPayload(data.raw_response);
  if (data.data) return unwrapCasPayload(data.data);
  return data;
}

export function toNumber(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const n = parseFloat(String(value).replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
}

export function formatInr(value, { digits = 0 } = {}) {
  const n = toNumber(value);
  if (n == null) return '—';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
  }).format(n);
}

export function formatQty(value, digits = 3) {
  const n = toNumber(value);
  if (n == null) return '—';
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  }).format(n);
}

export function formatPct(value) {
  const n = toNumber(value);
  if (n == null) return '—';
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(2)}%`;
}

export function formatDate(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function formatPeriod(from, to) {
  const a = formatDate(from);
  const b = formatDate(to);
  if (a && b) return `${a} – ${b}`;
  return a || b || null;
}

const DEMAT_BUCKETS = [
  ['equities', 'Equity'],
  ['demat_mutual_funds', 'Demat MF'],
  ['mutual_funds', 'Demat MF'],
  ['aifs', 'AIF'],
];

const BOND_BUCKETS = [
  ['corporate_bonds', 'Corporate Bond'],
  ['government_securities', 'G-Sec'],
];

function flattenDematBuckets(root, buckets, keyPrefix) {
  const accounts = Array.isArray(root?.demat_accounts) ? root.demat_accounts : [];
  const rows = [];
  accounts.forEach((account, ai) => {
    const holdings = account?.holdings && typeof account.holdings === 'object' ? account.holdings : {};
    buckets.forEach(([key, label]) => {
      const list = Array.isArray(holdings[key]) ? holdings[key] : [];
      list.forEach((item, ii) => {
        const info = item?.additional_info && typeof item.additional_info === 'object'
          ? item.additional_info
          : {};
        rows.push({
          key: `${keyPrefix}-${account.bo_id || account.client_id || ai}-${key}-${item.isin || ii}`,
          type: label,
          name: item.name || '—',
          isin: item.isin || '—',
          dp: account.dp_name || '—',
          account: account.bo_id || account.client_id || account.dp_id || '—',
          units: toNumber(item.units),
          value: toNumber(item.value),
          couponRate: toNumber(info.coupon_rate ?? item.coupon_rate ?? item.coupon),
          maturityDate: info.maturity_date || item.maturity_date || item.maturity || null,
          transactions: Array.isArray(item.transactions) ? item.transactions : [],
        });
      });
    });
  });
  return rows;
}

export function flattenMutualFunds(root) {
  const folios = Array.isArray(root?.mutual_funds)
    ? root.mutual_funds
    : Array.isArray(root?.folios)
      ? root.folios
      : [];

  const rows = [];
  folios.forEach((folio, fi) => {
    const schemes = Array.isArray(folio.schemes) && folio.schemes.length
      ? folio.schemes
      : [folio];
    schemes.forEach((scheme, si) => {
      const invested = toNumber(scheme.cost);
      const current = toNumber(scheme.value);
      const gain = toNumber(scheme.gain?.absolute)
        ?? (invested != null && current != null ? current - invested : null);
      const returnPct = toNumber(scheme.gain?.percentage)
        ?? (invested ? (gain / invested) * 100 : null);
      rows.push({
        key: `mf-${folio.folio_number || fi}-${scheme.isin || si}`,
        amc: folio.amc || scheme.amc || '—',
        scheme: scheme.name || scheme.scheme_name || folio.scheme_name || '—',
        folio: folio.folio_number || scheme.folio_number || '—',
        type: scheme.type || 'Other',
        units: toNumber(scheme.units),
        nav: toNumber(scheme.nav),
        invested,
        current,
        gain,
        returnPct,
        isin: scheme.isin || '',
        transactions: Array.isArray(scheme.transactions) ? scheme.transactions : [],
      });
    });
  });
  return rows;
}

export function flattenDemat(root) {
  return flattenDematBuckets(root, DEMAT_BUCKETS, 'dm');
}

export function flattenBonds(root) {
  return flattenDematBuckets(root, BOND_BUCKETS, 'bd');
}

export function flattenNps(root) {
  const accounts = Array.isArray(root?.nps) ? root.nps : [];
  const rows = [];
  accounts.forEach((account, ai) => {
    const funds = Array.isArray(account.funds) && account.funds.length ? account.funds : [account];
    funds.forEach((fund, fi) => {
      const cost = toNumber(fund.cost);
      const value = toNumber(fund.value);
      rows.push({
        key: `nps-${account.pran || ai}-${fi}`,
        pran: account.pran || '—',
        cra: account.cra || '—',
        fund: fund.name || '—',
        tier: fund.additional_info?.tier ?? '—',
        manager: fund.additional_info?.manager || '—',
        units: toNumber(fund.units),
        nav: toNumber(fund.nav),
        invested: cost,
        current: value,
        gain: cost != null && value != null ? value - cost : null,
      });
    });
  });
  return rows;
}

export function flattenInsurance(root) {
  const policies = root?.insurance?.life_insurance_policies
    || root?.insurance?.policies
    || (Array.isArray(root?.insurance) ? root.insurance : []);
  return (Array.isArray(policies) ? policies : []).map((policy, i) => ({
    key: `ins-${policy.policy_number || i}`,
    provider: policy.provider || '—',
    policy: policy.policy_name || '—',
    number: policy.policy_number || '—',
    lifeAssured: policy.life_assured || '—',
    status: policy.status || '—',
    sumAssured: toNumber(policy.sum_assured),
    premium: toNumber(policy.premium_amount),
    frequency: policy.premium_frequency || '—',
  }));
}

export function summarizeCas(root) {
  const mfRows = flattenMutualFunds(root);
  const dematRows = flattenDemat(root);
  const bondRows = flattenBonds(root);
  const npsRows = flattenNps(root);
  const insuranceRows = flattenInsurance(root);

  const mfInvested = mfRows.reduce((s, r) => s + (r.invested || 0), 0);
  const mfCurrent = mfRows.reduce((s, r) => s + (r.current || 0), 0);
  const mfGain = mfCurrent - mfInvested;
  const mfGainPct = mfInvested ? (mfGain / mfInvested) * 100 : null;

  const dematValue = dematRows.reduce((s, r) => s + (r.value || 0), 0);
  const bondValue = bondRows.reduce((s, r) => s + (r.value || 0), 0);
  const npsValue = npsRows.reduce((s, r) => s + (r.current || 0), 0);
  const insuranceValue = insuranceRows.reduce((s, r) => s + (r.sumAssured || 0), 0);

  const accounts = root?.summary?.accounts || {};
  const totalValue = toNumber(root?.summary?.total_value)
    ?? (mfCurrent + dematValue + bondValue + npsValue);

  return {
    mfRows,
    dematRows,
    bondRows,
    npsRows,
    insuranceRows,
    mfCount: mfRows.length || toNumber(accounts.mutual_funds?.count) || 0,
    dematCount: dematRows.length,
    bondCount: bondRows.length,
    npsCount: npsRows.length || toNumber(accounts.nps?.count) || 0,
    insuranceCount: insuranceRows.length || toNumber(accounts.insurance?.count) || 0,
    mfInvested,
    mfCurrent: toNumber(accounts.mutual_funds?.total_value) ?? mfCurrent,
    mfGain,
    mfGainPct,
    dematValue,
    bondValue,
    npsValue: toNumber(accounts.nps?.total_value) ?? npsValue,
    insuranceValue: toNumber(accounts.insurance?.total_value) ?? insuranceValue,
    totalValue,
  };
}

export function csvEscape(value) {
  if (value == null || value === '') return '';
  const s = String(value);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export function downloadCsv(filename, columns, rows) {
  const header = columns.map((c) => csvEscape(c.label)).join(',');
  const body = rows.map((row) => columns.map((c) => csvEscape(c.get(row))).join(',')).join('\n');
  const blob = new Blob([`${header}\n${body}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function downloadJson(filename, data) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function amcColor(name) {
  const palette = ['#2563eb', '#7c3aed', '#0d9488', '#ca8a04', '#dc2626', '#ea580c', '#0891b2', '#4f46e5'];
  const str = String(name || '');
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return palette[Math.abs(hash) % palette.length];
}

export function includesFilter(rowValue, query) {
  if (!query) return true;
  return String(rowValue || '').toLowerCase().includes(String(query).toLowerCase());
}
